/**
 * Log.js - Componente per la gestione e visualizzazione dei log di validazione dei frame
 * 
 * Questo componente gestisce la visualizzazione e il filtraggio dei log relativi alla validazione
 * dei frame degli occhiali. Fornisce funzionalità per:
 * 
 * - Visualizzazione dei log in una tabella interattiva
 * - Filtraggio per intervallo di date
 * - Filtraggio per modellista
 * - Filtraggio per stato (Completato, In Progress, Declinato)
 * - Ricerca per nome prodotto o SKU
 * - Calcolo automatico dei tempi di lavorazione (escludendo weekend e festività)
 * 
 * Il componente si interfaccia con Firebase per:
 * - Recuperare i log dalla collezione "LogVariant"
 * - Recuperare i profili dei modellisti dalla collezione "Profiles"
 */

import React, { useState, useEffect, useMemo } from "react";
import HeaderComponent from "../../components/Components/Headers/HeaderComponent";
import { db } from "../../data/base";
import { collection, getDocs, Timestamp, query, where } from "firebase/firestore";
import { DatePicker, Space, Button, Select, Row, Col, Table, Input } from 'antd';
import locale from 'antd/es/date-picker/locale/en_US';
import { CatalogSummary } from "../../components/Components/Tables/TableBox";
import { InfoCircleOutlined, ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import CustomTooltip from "../../components/Components/Modals/CustomTooltip";
import { useNavigate } from "react-router-dom";
import DownloadLog from "./DownloadLog";
import { secureGetItem } from "../../data/utils";

const { RangePicker } = DatePicker;
const { Option } = Select;

function Log() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [modellisti, setModellisti] = useState([]);
    const [selectedModellista, setSelectedModellista] = useState(null);
    const [loadingModellisti, setLoadingModellisti] = useState(true);
    const [isCurrentMonth, setIsCurrentMonth] = useState(true);
    const [statusFilter, setStatusFilter] = useState(null);
    const [activeTab, setActiveTab] = useState('ALL');
    const [searchTerm, setSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'desc'
    });
    const navigate = useNavigate();

    // Recupera email e ruolo dell'utente corrente
    const currentUserEmail = secureGetItem("email");
    const currentUserRole = secureGetItem("role");

    // Verifica se l'utente corrente è un modellista
    const isCurrentUserModellista = currentUserRole === "Modellista" || currentUserRole === "ModellerSupervisor";

    // Fetch logs da Firebase
    const fetchLogs = async (startDate, endDate) => {
        if (!startDate || !endDate) return;

        try {
            setLoading(true);

            // Configura le date iniziale e finale per includere tutto il giorno
            const dataInizio = new Date(startDate);
            const dataFine = new Date(endDate);

            dataInizio.setHours(0, 0, 0, 0);
            dataFine.setHours(23, 59, 59, 999);

            const timestampInizio = Timestamp.fromDate(dataInizio);
            const timestampFine = Timestamp.fromDate(dataFine);

            // Query per ottenere i log nell'intervallo di date
            const logRef = collection(db, "LogVariant");
            const logsSnapshot = await getDocs(logRef);

            const logsData = [];
            logsSnapshot.forEach((doc) => {
                const data = doc.data();

                // Verifica se logInitVariantDate OPPURE logCompleteVariantDate è nell'intervallo
                const initDateInRange = data.logInitVariantDate &&
                    data.logInitVariantDate >= timestampInizio &&
                    data.logInitVariantDate <= timestampFine;

                const completeDateInRange = data.logCompleteVariantDate &&
                    data.logCompleteVariantDate >= timestampInizio &&
                    data.logCompleteVariantDate <= timestampFine;

                // Aggiungi il log se almeno una delle due date è nel range
                if (initDateInRange || completeDateInRange) {

                    logsData.push({
                        id: doc.id,
                        ...data
                    });
                } else {

                }
            });

            // Ordina i log per data decrescente (più recenti prima)
            logsData.sort((a, b) => {
                // Verifica che entrambi gli oggetti abbiano logInitVariantDate e seconds
                if (!a.logInitVariantDate || !a.logInitVariantDate.seconds) {
                    return 1; // Sposta 'a' in fondo se non ha data valida
                }
                if (!b.logInitVariantDate || !b.logInitVariantDate.seconds) {
                    return -1; // Sposta 'b' in fondo se non ha data valida
                }
                // Ordinamento normale se entrambi hanno date valide
                return b.logInitVariantDate.seconds - a.logInitVariantDate.seconds;
            });

            setLogs(logsData);
        } catch (err) {
            console.error("Errore nel recupero dei log:", err);
            setError("Si è verificato un errore nel recupero dei log");
        } finally {
            setLoading(false);
        }
    };

    // Fetch modellisti da Firebase
    useEffect(() => {
        const fetchModellisti = async () => {
            try {
                setLoadingModellisti(true);
                const profilesRef = collection(db, "Profiles");

                // Query per Modellista
                const modellistiQuery = query(profilesRef, where("role", "==", "Modellista"));
                const modellistiSnapshot = await getDocs(modellistiQuery);

                // Query per ModellerSupervisor
                const supervisorQuery = query(profilesRef, where("role", "==", "ModellerSupervisor"));
                const supervisorSnapshot = await getDocs(supervisorQuery);

                const modellistiData = [];

                // Aggiungi i Modellista
                modellistiSnapshot.forEach((doc) => {
                    const data = doc.data();
                    if (data._email) {
                        modellistiData.push({
                            email: data._email,
                            nome: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data._email,
                            role: "Modellista"
                        });
                    }
                });

                // Aggiungi i ModellerSupervisor
                supervisorSnapshot.forEach((doc) => {
                    const data = doc.data();
                    if (data._email) {
                        modellistiData.push({
                            email: data._email,
                            nome: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data._email,
                            role: "ModellerSupervisor"
                        });
                    }
                });

                setModellisti(modellistiData);

                // Se l'utente corrente è un modellista, selezionalo automaticamente
                if (isCurrentUserModellista && currentUserEmail) {
                    const currentUser = modellistiData.find(modellista =>
                        modellista.email.toLowerCase() === currentUserEmail.toLowerCase()
                    );
                    if (currentUser) {
                        setSelectedModellista(currentUser.email);
                    }
                }
            } catch (err) {
                console.error("Errore nel recupero dei modellisti:", err);
            } finally {
                setLoadingModellisti(false);
            }
        };

        fetchModellisti();
    }, [isCurrentUserModellista, currentUserEmail]);

    // Handler per il cambio del modellista selezionato
    const handleModellistaChange = (value) => {
        // Se l'utente corrente è un modellista, non permettere il cambio
        if (isCurrentUserModellista) {
            return;
        }

        setSelectedModellista(value === "all" ? null : value);
        // Reset dello stato attivo quando si cambia modellista
        setActiveTab('ALL');
        setStatusFilter(null);
    };

    // Fetch in base all'intervallo di date selezionato
    useEffect(() => {
        if (dateRange && dateRange.length === 2) {
            fetchLogs(dateRange[0], dateRange[1]);
        }
    }, [dateRange]);

    // Imposta l'intervallo predefinito (primo e ultimo giorno del mese corrente)
    useEffect(() => {
        const oggi = new Date();
        const primoGiornoMese = new Date(oggi.getFullYear(), oggi.getMonth(), 1);
        const ultimoGiornoMese = new Date(oggi.getFullYear(), oggi.getMonth() + 1, 0);
        setDateRange([primoGiornoMese, ultimoGiornoMese]);
    }, []);

    // Handler per il cambio dell'intervallo di date
    const handleRangeChange = (dates) => {
        if (dates && dates.length === 2) {
            // In Ant Design 4.x, dates è già un array di oggetti moment/dayjs
            // Convertiamo in Date JavaScript
            setDateRange([dates[0].toDate(), dates[1].toDate()]);
            setIsCurrentMonth(false);
        } else {
            setDateRange(null);
            // Quando l'utente cancella le date, reimpostiamo al mese corrente
            const oggi = new Date();
            const primoGiornoMese = new Date(oggi.getFullYear(), oggi.getMonth(), 1);
            const ultimoGiornoMese = new Date(oggi.getFullYear(), oggi.getMonth() + 1, 0);
            setDateRange([primoGiornoMese, ultimoGiornoMese]);
            setIsCurrentMonth(true);
        }
    };

    // Handler per reimpostare l'intervallo all'intero mese corrente
    const handleResetToCurrentMonth = () => {
        const oggi = new Date();
        const primoGiornoMese = new Date(oggi.getFullYear(), oggi.getMonth(), 1);
        const ultimoGiornoMese = new Date(oggi.getFullYear(), oggi.getMonth() + 1, 0);
        setDateRange([primoGiornoMese, ultimoGiornoMese]);
        setIsCurrentMonth(true);
    };

    // Formatta l'intervallo di date per la visualizzazione
    const formatDateRange = () => {
        if (!dateRange || dateRange.length !== 2) return "No range selected";

        const formatSingleDate = (date) => {
            return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
        };

        return `${formatSingleDate(dateRange[0])} - ${formatSingleDate(dateRange[1])}`;
    };

    // Stili CSS per i diversi stati
    const getStatusClass = (status) => {
        if (status === "Complete") {
            return "status-complete";
        } else if (status === "Pending") {
            return "status-pending";
        } else {
            return "status-unknown";
        }
    };

    // Stili CSS per i diversi stati
    const getStatusText = (status) => {
        if (status === "Complete") {
            return "Completed";
        } else if (status === "Pending") {
            return "In Progress";
        } else {
            return status || "Unknown";
        }
    };

    // Stili CSS per la tabella e gli stati
    const tableStyles = `
        .logs-table {
            width: 100%;
            border-collapse: collapse;
        }
        .logs-table th, .logs-table td {
            padding: 8px;
            border: 1px solid #ddd;
            text-align: left;
        }
        .logs-table th {
            background-color: #f2f2f2;
        }
        .logs-table tr {
            background-color: #ffffff;
        }
        .status-complete {
            background-color: #d4edda;
            padding: 5px 10px;
            border-radius: 3px;
            color: #155724;
            display: inline-block;
        }
        .status-pending {
            background-color: #fff3cd;
            padding: 5px 10px;
            border-radius: 3px;
            color: #856404;
            display: inline-block;
        }
        .status-unknown {
            background-color: #e2e3e5;
            padding: 5px 10px;
            border-radius: 3px;
            color: #383d41;
            display: inline-block;
        }
    `;

    // Filtra i log in base ai criteri selezionati (modellista e stato)
    const filteredByModellerLogs = useMemo(() => {
        return selectedModellista
            ? logs.filter(log =>
                log.list_variant_assigned &&
                log.list_variant_assigned.length > 0 &&
                log.list_variant_assigned[log.list_variant_assigned.length - 1].assignedAccount === selectedModellista
            )
            : logs;
    }, [logs, selectedModellista]);

    // Filtra i log in base alla ricerca di prodotto o SKU
    const filteredBySearchLogs = useMemo(() => {
        if (!searchTerm.trim()) return filteredByModellerLogs;

        return filteredByModellerLogs.filter(log => {
            const productNameMatch = log.productName && log.productName.toLowerCase().includes(searchTerm.toLowerCase());
            const skuMatch = log.sku && log.sku.toLowerCase().includes(searchTerm.toLowerCase());
            return productNameMatch || skuMatch;
        });
    }, [filteredByModellerLogs, searchTerm]);

    // Calcola i conteggi degli stati
    const statusCounts = useMemo(() => {
        const completedCount = filteredBySearchLogs.filter(log => log.logStatus === "Complete").length;
        const pendingCount = filteredBySearchLogs.filter(log => log.logStatus === "Pending").length;

        // Conteggio per i log declinati da Spaarkly (Admin o ModellerSupervisor)
        const declinedBySpaarklyCount = filteredBySearchLogs.filter(log => {
            if (!log.list_decline_variant || !Array.isArray(log.list_decline_variant)) return false;

            return log.list_decline_variant.some(decline => {
                if (!decline || typeof decline !== 'object') return false;
                return decline.role === "ModellerSupervisor" || decline.role === "Admin";
            });
        }).length;

        // Conteggio per i log declinati dal cliente
        const declinedByClientCount = filteredBySearchLogs.filter(log => {
            if (!log.list_decline_variant || !Array.isArray(log.list_decline_variant)) return false;

            return log.list_decline_variant.some(decline => {
                if (!decline || typeof decline !== 'object') return false;
                return decline.role === "Cliente";
            });
        }).length;

        return {
            ALL: filteredBySearchLogs.length,
            Complete: completedCount,
            Pending: pendingCount,
            DeclinedBySpaarkly: declinedBySpaarklyCount,
            DeclinedByClient: declinedByClientCount
        };
    }, [filteredBySearchLogs]);

    // Dati per il CatalogSummary
    const summaryData = useMemo(() => [
        { key: 'ALL', label: 'ALL', count: statusCounts.ALL },
        { key: 'Complete', label: 'COMPLETE', count: statusCounts.Complete },
        { key: 'Pending', label: 'PENDING', count: statusCounts.Pending },
        { key: 'DeclinedBySpaarkly', label: 'DECLINED BY SPAARKLY', count: statusCounts.DeclinedBySpaarkly },
        { key: 'DeclinedByClient', label: 'DECLINED BY CLIENT', count: statusCounts.DeclinedByClient }
    ], [statusCounts]);

    // Componente per la tabella dei log
    const LogsTable = () => {
        if (loading) {
            return (
                <div style={{ textAlign: "center", padding: "20px" }}>
                    Loading...
                </div>
            );
        }

        if (error) {
            return (
                <div style={{ padding: "10px 15px", backgroundColor: "#f8d7da", borderRadius: "4px", color: "#721c24" }}>
                    {error}
                </div>
            );
        }

        // Filtra per stato se è selezionato uno stato specifico
        let displayLogs = filteredBySearchLogs;

        if (statusFilter === 'DeclinedBySpaarkly') {
            displayLogs = filteredBySearchLogs.filter(log => {
                if (!log.list_decline_variant || !Array.isArray(log.list_decline_variant)) return false;

                return log.list_decline_variant.some(decline => {
                    if (!decline || typeof decline !== 'object') return false;
                    return decline.role === "ModellerSupervisor" || decline.role === "Admin";
                });
            });
        } else if (statusFilter === 'DeclinedByClient') {
            displayLogs = filteredBySearchLogs.filter(log => {
                if (!log.list_decline_variant || !Array.isArray(log.list_decline_variant)) return false;

                return log.list_decline_variant.some(decline => {
                    if (!decline || typeof decline !== 'object') return false;
                    return decline.role === "Cliente";
                });
            });
        } else if (statusFilter) {
            displayLogs = filteredBySearchLogs.filter(log => log.logStatus === statusFilter);
        }

        if (displayLogs.length === 0) {
            return (
                <div style={{ padding: "10px 15px", backgroundColor: "#d1ecf1", borderRadius: "4px", color: "#0c5460" }}>
                    No logs found for this date range
                    {selectedModellista ? " and selected modeller" : ""}
                    {statusFilter ? ` with status ${statusFilter}` : ""}
                </div>
            );
        }

        // Definizione delle colonne della tabella
        const columns = [
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <span>Product</span>
                        <CustomTooltip title="Name of the product">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                dataIndex: 'productName',
                key: 'productName',
                render: (text, record) => record.productName || "N/A",
                align: 'left',
            },
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <span>SKU</span>
                        <CustomTooltip title="SKU identifier of the product">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                dataIndex: 'sku',
                key: 'sku',
                render: (text, record) => record.sku || "N/A",
                align: 'left',
            },
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span>Status</span>
                        <CustomTooltip title="Current status of the validation">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                dataIndex: 'logStatus',
                key: 'logStatus',
                render: (text, record) => (
                    <div className={getStatusClass(record.logStatus)}>
                        {getStatusText(record.logStatus)}
                    </div>
                ),
                align: 'center',
                sorter: (a, b) => {
                    // Ordina in modo che "Complete" venga sempre prima di "Pending"
                    if (a.logStatus === "Complete" && b.logStatus !== "Complete") return -1;
                    if (a.logStatus !== "Complete" && b.logStatus === "Complete") return 1;
                    return 0;
                },
                defaultSortOrder: 'ascend',
            },
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span>Type of Modeling</span>
                        <CustomTooltip title="Modeling type used for this variant">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                key: 'typeOfModeling',
                render: (text, record) => {
                    if (record.list_validate_variant && record.list_validate_variant.length > 0 && record.list_validate_variant[0].type_of_modeling) {
                        return record.list_validate_variant[0].type_of_modeling;
                    }
                    return "N/A";
                },
                align: 'center',
            },
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <span>Completion Date</span>
                        <CustomTooltip title="Date when the validation was completed">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                key: 'completeDate',
                render: (text, record) => {

                    if (record.logCompleteVariantDate) {
                        try {

                            // Verifica se il timestamp ha una struttura diversa
                            if (typeof record.logCompleteVariantDate === 'object' && record.logCompleteVariantDate._seconds) {
                                const date = new Date(record.logCompleteVariantDate._seconds * 1000);
                                const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
                                return formattedDate;
                            }

                            // Utilizza direttamente i secondi del timestamp per creare una data JavaScript
                            const date = new Date(record.logCompleteVariantDate.seconds * 1000);

                            const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
                            return formattedDate;
                        } catch (error) {
                            console.error("Errore nella conversione del timestamp:", error);
                            console.error("Timestamp problematico:", record.logCompleteVariantDate);
                            return "Errore Data";
                        }
                    }
                    return "N/A";
                },
                sorter: (a, b) => {
                    if (!a.logCompleteVariantDate || !b.logCompleteVariantDate) return 0;
                    return a.logCompleteVariantDate.seconds - b.logCompleteVariantDate.seconds;
                },
                align: 'right',
            },
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <span>Work Time First Validation</span>
                        <CustomTooltip title="Time taken for the first validation (work hours only)">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                key: 'firstValidationWork',
                render: (text, record) => {
                    if (record.list_validate_variant && record.list_validate_variant.length > 0 && record.logInitVariantDate) {
                        // Prendi il timestamp della prima validazione
                        const firstValidation = record.list_validate_variant[0].time;
                        // Calcola il tempo di lavoro tra l'inizializzazione e la prima validazione
                        return calculateWorkTime(record.logInitVariantDate, firstValidation);
                    }
                    return "N/A";
                },
                align: 'right',
            },
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <span>Start Initialization Date</span>
                        <CustomTooltip title="Date when the validation was started">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                key: 'initDate',
                render: (text, record) => {

                    if (record.logInitVariantDate) {
                        try {
                            // Verifica se il timestamp ha una struttura diversa
                            if (typeof record.logInitVariantDate === 'object' && record.logInitVariantDate._seconds) {
                                const date = new Date(record.logInitVariantDate._seconds * 1000);
                                const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
                                return formattedDate;
                            }

                            // Utilizza direttamente i secondi del timestamp per creare una data JavaScript
                            const date = new Date(record.logInitVariantDate.seconds * 1000);

                            const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
                            return formattedDate;
                        } catch (error) {
                            console.error("Errore nella conversione del timestamp:", error);
                            console.error("Timestamp problematico:", record.logInitVariantDate);
                            return "Errore Data";
                        }
                    }
                    return "N/A";
                },
                sorter: (a, b) => {
                    if (!a.logInitVariantDate || !b.logInitVariantDate) return 0;
                    return a.logInitVariantDate.seconds - b.logInitVariantDate.seconds;
                },
                align: 'right',
            },
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <span>End First Validation Date</span>
                        <CustomTooltip title="Date of the first validation">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                key: 'firstValidationDate',
                render: (text, record) => {
                    if (record.list_validate_variant && record.list_validate_variant.length > 0 && record.list_validate_variant[0].time) {
                        try {
                            // Verifica se il timestamp ha una struttura diversa
                            if (typeof record.list_validate_variant[0].time === 'object' && record.list_validate_variant[0].time._seconds) {
                                const date = new Date(record.list_validate_variant[0].time._seconds * 1000);
                                const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
                                return formattedDate;
                            }

                            // Utilizza direttamente i secondi del timestamp per creare una data JavaScript
                            const date = new Date(record.list_validate_variant[0].time.seconds * 1000);

                            const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
                            return formattedDate;
                        } catch (error) {
                            console.error("Errore nella conversione del timestamp della prima validazione:", error);
                            return "Errore Data";
                        }
                    }
                    return "N/A";
                },
                align: 'right',
            },
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <span>Work Complete Time</span>
                        <CustomTooltip title="Total work time from initialization to completion (work hours only)">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                key: 'workComplete',
                render: (text, record) => {
                    if (record.logInitVariantDate && record.logCompleteVariantDate) {
                        // Calcola il tempo di lavoro tra l'inizializzazione e il completamento
                        return calculateWorkTime(record.logInitVariantDate, record.logCompleteVariantDate);
                    }
                    return "N/A";
                },
                align: 'right',
            },
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span>Decline by Spaarkly</span>
                        <CustomTooltip title="Number of times declined by Spaarkly (Admin or ModellerSupervisor)">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                key: 'declineBySpaarkly',
                render: (_, record) => {
                    if (!record.list_decline_variant || !Array.isArray(record.list_decline_variant)) {
                        return 0;
                    }

                    const count = record.list_decline_variant.filter(decline =>
                        decline && typeof decline === 'object' &&
                        (decline.role === "Admin" || decline.role === "ModellerSupervisor")
                    ).length;

                    return count;
                },
                align: 'center',
            },
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span>Decline by Client</span>
                        <CustomTooltip title="Number of times declined by the client">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                key: 'declineByClient',
                render: (_, record) => {
                    if (!record.list_decline_variant || !Array.isArray(record.list_decline_variant)) {
                        return 0;
                    }

                    const count = record.list_decline_variant.filter(decline =>
                        decline && typeof decline === 'object' && decline.role === "Cliente"
                    ).length;

                    return count;
                },
                align: 'center',
            },
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <span>Assigned to</span>
                        <CustomTooltip title="Name of the modeller assigned to this task">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                dataIndex: 'list_variant_assigned',
                key: 'assignedAccount',
                render: (list_variant_assigned) => {
                    if (list_variant_assigned && list_variant_assigned.length > 0) {
                        const assignedEmail = list_variant_assigned[list_variant_assigned.length - 1].assignedAccount;
                        // Find the user in the modellisti array to get firstName and lastName
                        const assignedUser = modellisti.find(user => user.email === assignedEmail);
                        if (assignedUser) {
                            return `${assignedUser.nome}`;
                        }
                        return assignedEmail; // Fallback to email if user not found
                    }
                    return "N/A";
                },
                align: 'right',
            },
        ];

        return (
            <>
                <style>{tableStyles}</style>
                <Table
                    columns={columns}
                    dataSource={displayLogs}
                    rowKey="id"
                    pagination={{
                        defaultPageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: [10, 25, 50, 100],
                    }}
                    size="small"
                    showSorterTooltip={false}
                    scroll={{ x: 900 }}
                />
            </>
        );
    };

    // Funzione per cambiare tab
    const handleTabChange = (tabKey) => {
        if (tabKey === 'ALL') {
            setStatusFilter(null);
            setActiveTab('ALL');
        } else {
            setStatusFilter(tabKey);
            setActiveTab(tabKey);
        }
    };

    // Funzione per calcolare la data di Pasqua
    const calculateEaster = (year) => {
        const f = Math.floor,
            // Anno
            G = year % 19,
            C = f(year / 100),
            H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30,
            I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11)),
            J = (year + f(year / 4) + I + 2 - C + f(C / 4)) % 7,
            L = I - J,
            month = 3 + f((L + 40) / 44),
            day = L + 28 - 31 * f(month / 4);
        return new Date(year, month - 1, day);
    };

    // Funzione per ottenere le festività italiane per un anno specifico
    const getItalianHolidays = (year) => {
        const easter = calculateEaster(year);
        const easterMonday = new Date(easter);
        easterMonday.setDate(easter.getDate() + 1);

        return [
            new Date(year, 0, 1),  // Capodanno
            new Date(year, 0, 6),  // Epifania
            easter,                // Pasqua
            easterMonday,          // Lunedì dell'Angelo
            new Date(year, 3, 25), // Festa della Liberazione
            new Date(year, 4, 1),  // Festa dei Lavoratori
            new Date(year, 5, 2),  // Festa della Repubblica
            new Date(year, 7, 15), // Ferragosto
            new Date(year, 10, 1), // Ognissanti
            new Date(year, 11, 8), // Immacolata Concezione
            new Date(year, 11, 25),// Natale
            new Date(year, 11, 26) // Santo Stefano
        ];
    };

    // Funzione per verificare se una data è un giorno festivo
    const isHoliday = (date) => {
        const holidays = getItalianHolidays(date.getFullYear());
        return holidays.some(holiday =>
            holiday.getDate() === date.getDate() &&
            holiday.getMonth() === date.getMonth()
        );
    };

    // Funzione per calcolare il tempo di lavoro DELLA PRIMA VALIDAZIONE tra due date (logInitVariantDate e list_validate_variant[0].time)
    // rispettando l'orario lavorativo Lunedi-Venerdi dalle 9 alle 18 con pausa pranzo dalle 13.5 alle 14.5
    const calculateWorkTime = (startDate, endDate) => {
        if (!startDate || !endDate) return "N/A";

        try {
            let start, end;

            // Gestione di diversi formati di timestamp
            if (typeof startDate === 'object' && startDate._seconds) {
                start = new Date(startDate._seconds * 1000);
            } else if (typeof startDate === 'object' && startDate.seconds) {
                start = new Date(startDate.seconds * 1000);
            } else if (startDate instanceof Date) {
                start = startDate;
            } else {
                return "N/A";
            }

            if (typeof endDate === 'object' && endDate._seconds) {
                end = new Date(endDate._seconds * 1000);
            } else if (typeof endDate === 'object' && endDate.seconds) {
                end = new Date(endDate.seconds * 1000);
            } else if (endDate instanceof Date) {
                end = endDate;
            } else {
                return "N/A";
            }

            // Se la data di fine è precedente alla data di inizio, restituisci N/A
            if (end < start) return "N/A";

            // Costanti per orari lavorativi
            const WORK_START_HOUR = 9; // 9:00
            const WORK_END_HOUR = 18; // 18:00
            const LUNCH_START_HOUR = 13.5; // 13:30
            const LUNCH_END_HOUR = 14.5; // 14:30
            const MS_PER_HOUR = 3600000; // millisecondi in un'ora

            let totalWorkTimeMs = 0;
            let currentDate = new Date(start);

            // Imposta l'ora di inizio al primo orario lavorativo disponibile
            if (currentDate.getHours() < WORK_START_HOUR) {
                currentDate.setHours(WORK_START_HOUR, 0, 0, 0);
            } else if (currentDate.getHours() >= WORK_END_HOUR) {
                // Se inizia dopo l'orario lavorativo, passa al giorno successivo
                currentDate.setDate(currentDate.getDate() + 1);
                currentDate.setHours(WORK_START_HOUR, 0, 0, 0);
            }

            // Continua finché non raggiungiamo la data di fine
            while (currentDate < end) {
                const dayOfWeek = currentDate.getDay();

                // Salta i weekend e i giorni festivi
                if (dayOfWeek === 0 || dayOfWeek === 6 || isHoliday(currentDate)) {
                    currentDate.setDate(currentDate.getDate() + 1);
                    currentDate.setHours(WORK_START_HOUR, 0, 0, 0);
                    continue;
                }

                // Calcola la fine della giornata lavorativa corrente o la data di fine
                let workDayEnd = new Date(currentDate);
                workDayEnd.setHours(WORK_END_HOUR, 0, 0, 0);

                // Se la data di fine è prima della fine della giornata lavorativa corrente
                if (end < workDayEnd) {
                    workDayEnd = new Date(end);
                }

                // Gestione della pausa pranzo
                if (currentDate.getHours() < LUNCH_START_HOUR && workDayEnd.getHours() >= LUNCH_END_HOUR) {
                    // Aggiungi il tempo fino alla pausa pranzo
                    const timeBeforeLunch = new Date(currentDate);
                    timeBeforeLunch.setHours(LUNCH_START_HOUR, 0, 0, 0);
                    totalWorkTimeMs += timeBeforeLunch - currentDate;

                    // Aggiungi il tempo dopo la pausa pranzo fino alla fine della giornata
                    const timeAfterLunch = new Date(currentDate);
                    timeAfterLunch.setHours(LUNCH_END_HOUR, 0, 0, 0);

                    if (workDayEnd > timeAfterLunch) {
                        totalWorkTimeMs += workDayEnd - timeAfterLunch;
                    }
                }
                // Se l'orario di lavoro va dalla pausa pranzo alla fine della giornata
                else if (currentDate.getHours() >= LUNCH_END_HOUR && workDayEnd.getHours() >= LUNCH_END_HOUR) {
                    totalWorkTimeMs += workDayEnd - currentDate;
                }
                // Se l'orario di lavoro è tutto prima della pausa pranzo
                else if (currentDate.getHours() < LUNCH_START_HOUR && workDayEnd.getHours() <= LUNCH_START_HOUR) {
                    totalWorkTimeMs += workDayEnd - currentDate;
                }
                // Se l'orario di lavoro è interamente durante la pausa pranzo
                else if (currentDate.getHours() >= LUNCH_START_HOUR && workDayEnd.getHours() <= LUNCH_END_HOUR) {
                    // Non contiamo questo tempo
                }

                // Passa al giorno successivo
                if (workDayEnd >= end) {
                    break;
                } else {
                    currentDate = new Date(currentDate);
                    currentDate.setDate(currentDate.getDate() + 1);
                    currentDate.setHours(WORK_START_HOUR, 0, 0, 0);
                }
            }

            // Converti i millisecondi in un formato leggibile con giorni, mesi e anni
            const MS_PER_MINUTE = 60000;
            const MS_PER_SECOND = 1000;
            const HOURS_PER_DAY = 24;
            const DAYS_PER_MONTH = 30; // Approssimazione
            const MONTHS_PER_YEAR = 12;

            // Calcola i valori per le diverse unità di tempo
            let totalSeconds = Math.floor((totalWorkTimeMs % MS_PER_MINUTE) / MS_PER_SECOND);
            let totalMinutes = Math.floor((totalWorkTimeMs % MS_PER_HOUR) / MS_PER_MINUTE);
            let totalHours = Math.floor(totalWorkTimeMs / MS_PER_HOUR);

            // Se le ore superano 24, calcola giorni
            let totalDays = 0;
            let totalMonths = 0;
            let totalYears = 0;

            if (totalHours >= HOURS_PER_DAY) {
                totalDays = Math.floor(totalHours / HOURS_PER_DAY);
                totalHours = totalHours % HOURS_PER_DAY;

                // Se i giorni superano 30, calcola mesi
                if (totalDays >= DAYS_PER_MONTH) {
                    totalMonths = Math.floor(totalDays / DAYS_PER_MONTH);
                    totalDays = totalDays % DAYS_PER_MONTH;

                    // Se i mesi superano 12, calcola anni
                    if (totalMonths >= MONTHS_PER_YEAR) {
                        totalYears = Math.floor(totalMonths / MONTHS_PER_YEAR);
                        totalMonths = totalMonths % MONTHS_PER_YEAR;
                    }
                }
            }

            // Costruisci la stringa di output
            let timeString = '';

            if (totalYears > 0) {
                timeString += `${totalYears}y `;
            }

            if (totalMonths > 0) {
                timeString += `${totalMonths}mo `;
            }

            if (totalDays > 0) {
                timeString += `${totalDays}d `;
            }

            // Aggiungi sempre ore, minuti e secondi
            timeString += `${totalHours}h ${totalMinutes}m ${totalSeconds}s`;

            return timeString;

        } catch (error) {
            console.error("Errore nel calcolo del tempo di lavoro:", error);
            return "Errore";
        }
    };

    return (
        <div>
            <HeaderComponent
                title="Log"
                isVisible={true}
                buttonIcon={<NavigateBeforeIcon style={{ fontSize: '1.5rem' }} />}
                buttonText=""
                onButtonClick={() => navigate('/framevalidation')}
                buttonPosition="left"
                noCamp={true}
            />
            <div>
                <div>
                    <div style={{ padding: "10px 15px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            {/* Rigo selezione date */}
                            <div>
                                <div style={{ marginRight: "10px", fontWeight: "500" }}>Logs from: "{formatDateRange()}"</div>
                            </div>
                            <div>
                                <Space>
                                    <RangePicker
                                        locale={locale}
                                        format="DD/MM/YYYY"
                                        onChange={handleRangeChange}
                                        allowClear={true}
                                        style={{ border: !isCurrentMonth ? "1px solid #1890ff" : "" }}
                                        placeholder={["Start date", "End date"]}
                                    />
                                    <Button
                                        onClick={handleResetToCurrentMonth}
                                        type={isCurrentMonth ? "primary" : "default"}
                                    >
                                        Current Month
                                    </Button>
                                </Space>
                            </div>
                        </div>
                        {/* Rigo selezione modellista con bottone Download Log */}
                        <div style={{ marginTop: "15px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <div style={{ marginRight: "10px", fontWeight: "500" }}>
                                    {isCurrentUserModellista ? "Your Logs:" : "Filter by Modeller:"}
                                </div>
                                <Select
                                    style={{ width: 300 }}
                                    placeholder="Select a Modeller"
                                    onChange={handleModellistaChange}
                                    loading={loadingModellisti}
                                    disabled={isCurrentUserModellista} // Disabilita la select se l'utente è un modellista
                                    value={selectedModellista || "all"} // Imposta il valore selezionato
                                >
                                    <Option value="all">All Modeller</Option>
                                    {modellisti.map(modellista => (
                                        <Option key={modellista.email} value={modellista.email}>
                                            {modellista.nome}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                            <DownloadLog
                                filters={{
                                    dateRange,
                                    selectedModellista,
                                    statusFilter,
                                    searchTerm
                                }}
                            />
                        </div>

                        {/* Rigo ricerca prodotto */}
                        <div style={{ marginTop: "15px" }}>
                            <Input
                                placeholder="Search by Product name or SKU..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                prefix={<SearchOutlined style={{ color: '#aaa' }} />}
                                allowClear
                                style={{
                                    width: "100%",
                                    borderRadius: "0.5rem",
                                    fontSize: "clamp(0.75rem, 0.7rem + 0.25vw, 0.875rem)",
                                    padding: "clamp(0.375rem, 0.3rem + 0.5vw, 0.5rem)"
                                }}
                            />
                        </div>
                    </div>
                    {/* Rigo catalog summary */}
                    <Row gutter={[16, 16]} style={{ padding: "15px", marginBottom: '5px' }}>
                        <Col span={24}>
                            <CatalogSummary
                                data={summaryData}
                                activeTab={activeTab}
                                onTabChange={handleTabChange}
                            />
                        </Col>
                    </Row>
                    {/* Rigo tabella dei log */}
                    <div style={{ padding: "15px" }}>
                        <LogsTable />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Log;